import React, { memo, useCallback, useEffect, useState } from "react";
import ControlDetailsPanel from "./ControlDetailsPanel";
import { Levels, Icons } from "../ControlComfoair350";
import ToggleIcon from "../../../Components/ToggleIcon";
import { useSocketClient } from "../../../contexts/SocketClientProvider";
import { Container, Slider, Stack, useTheme } from "@mui/material";
import Comfoair350AttributeValue from "./Comfoair350AttributeValue";
import MaintenanceTable, { TMaintenanceItem } from "./MaintenanceTable";
import useControlAttribute from "../../../hooks/useControlAttribute";
import { useUser } from "../../../contexts/UserProvider";
import Comfoair350TemperatureChart from "./Comfoair350TemperatureChart";
import { TMode } from "../../../hooks/useModeColor";
import { IControlComfoair350 } from "../types";
import Comfoair350 from "./Comfoair350";
import Comfoair350FanChart from "./Comfoair350FanChart";

const Comfoair = memo(function Comfoair() {
  return <Comfoair350 />;
});

interface IControlDetailsComfoair350Props {
  control: IControlComfoair350;
}

const ControlDetailsComfoair350 = (props: IControlDetailsComfoair350Props) => {
  const { control } = props;
  const [iconNames, setIconNames] = useState(Icons);
  const tempComfort = useControlAttribute(control, "temp_comfort");
  const [temp, setTemp] = useState(tempComfort);
  const fan = useControlAttribute(control, "fan");
  const socketClient = useSocketClient();
  const { user } = useUser();

  useEffect(() => {
    setTemp(tempComfort);
  }, [tempComfort]);

  useEffect(() => {
    setIconNames(Icons);
  }, [fan]);

  const mode: TMode = fan === 0 ? "off" : "on";

  const { palette } = useTheme();
  const iconColor = (level: number) => {
    return level === fan ? palette.on.contrastText : palette.primary.main;
  };

  const setLevel = useCallback(
    (level: number) => {
      setIconNames(
        Icons.map((icon, i) => {
          return i === level ? "sync" : icon;
        }),
      );
      socketClient.process_command({
        type: "control",
        id: control.id,
        command: "set_fan",
        value: level,
      });
    },
    [control, socketClient],
  );

  const setTempComfort = useCallback(
    (temp: number) => {
      socketClient.process_command({
        type: "control",
        id: control.id,
        command: "set_temp_comfort",
        value: temp,
      });
    },
    [control, socketClient],
  );

  const items: TMaintenanceItem[] = [
    {
      name: "Replace dust filter",
      attribute: "dust_filter_replaced_timestamp",
    },
    {
      name: "Replace pollen filter",
      attribute: "pollen_filter_replaced_timestamp",
    },
  ];

  return (
    <ControlDetailsPanel mode={mode} title={control.name}>
      <div style={{ position: "relative", textAlign: "left" }}>
        <Comfoair />
        <Comfoair350AttributeValue control={control} attribute="temp_exhaust" />
        <Comfoair350AttributeValue control={control} attribute="temp_supply" />
        <Comfoair350AttributeValue control={control} attribute="temp_outside" />
        <Comfoair350AttributeValue control={control} attribute="temp_return" />
        <Comfoair350AttributeValue
          control={control}
          attribute="fan_state_exhaust_rpm"
        />
        <Comfoair350AttributeValue
          control={control}
          attribute="fan_state_intake_rpm"
        />
      </div>
      <Stack
        direction="row"
        justifyContent="center"
        alignItems="center"
        spacing={3}
      >
        {Levels.map((level) => (
          <ToggleIcon
            key={level}
            name={iconNames[level]}
            color={iconColor(level)}
            onClick={() => setLevel(level)}
          />
        ))}
      </Stack>
      {user?.is_operator && (
        <Container sx={{ paddingTop: 3 }}>
          <Slider
            value={temp}
            defaultValue={tempComfort}
            min={18}
            max={24}
            step={0.5}
            marks
            track={false}
            valueLabelDisplay="on"
            valueLabelFormat={(value) => `${value}°C`}
            onChange={(_, value) => setTemp(value)}
            onChangeCommitted={(_, value) => setTempComfort(value as number)}
          />
        </Container>
      )}
      <Comfoair350TemperatureChart control={control} width={300} height={100} />
      <Comfoair350FanChart control={control} width={300} height={60} />
      <MaintenanceTable
        control={control}
        items={items}
        reset={user?.is_operator || false}
      />
    </ControlDetailsPanel>
  );
};

export default ControlDetailsComfoair350;
